import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Calculator, ArrowLeft, DollarSign, Percent, Calendar } from "lucide-react";
import { useNavigate } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useTranslation } from "@/hooks/useTranslation";
import { useTheme } from "@/contexts/ThemeContext";

export default function LoanCalculatorPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { isDark } = useTheme();

  useEffect(() => { window.scrollTo(0, 0); }, []);

  const [formData, setFormData] = useState({ loanAmount: "", downPayment: "", interestRate: "", loanTerm: "" });
  const [monthlyPayment, setMonthlyPayment] = useState(null);
  const [totalPayment, setTotalPayment] = useState(null);
  const [totalInterest, setTotalInterest] = useState(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const calculateLoan = (e) => {
    e.preventDefault();
    const loanAmount = parseFloat(formData.loanAmount) || 0;
    const downPayment = parseFloat(formData.downPayment) || 0;
    const annualRate = parseFloat(formData.interestRate) || 0;
    const years = parseFloat(formData.loanTerm) || 0;
    const principal = loanAmount - downPayment;
    if (principal <= 0) { alert("Loan amount must be greater than down payment"); return; }
    if (annualRate <= 0 || years <= 0) { alert("Please enter valid interest rate and loan term"); return; }
    const monthlyRate = annualRate / 100 / 12;
    const numberOfPayments = years * 12;
    const monthlyPaymentAmount = (principal * monthlyRate * Math.pow(1 + monthlyRate, numberOfPayments)) / (Math.pow(1 + monthlyRate, numberOfPayments) - 1);
    const totalPaymentAmount = monthlyPaymentAmount * numberOfPayments;
    const totalInterestAmount = totalPaymentAmount - principal;
    setMonthlyPayment(monthlyPaymentAmount);
    setTotalPayment(totalPaymentAmount);
    setTotalInterest(totalInterestAmount);
  };

  const resetCalculator = () => {
    setFormData({ loanAmount: "", downPayment: "", interestRate: "", loanTerm: "" });
    setMonthlyPayment(null); setTotalPayment(null); setTotalInterest(null);
  };

  const inputCls = isDark ? "bg-white/5 border-white/10 text-white text-lg" : "bg-white border-gray-300 text-gray-900 text-lg";
  const panelCls = isDark ? "bg-white/5 backdrop-blur-sm border border-white/10 p-8" : "bg-white border border-gray-200 shadow-sm p-8";

  return (
    <div className={`min-h-screen transition-colors duration-300 ${isDark ? 'bg-[#050505]' : 'bg-[#FAFAFA]'}`}>
      <Navbar />
      
      <div className="pt-32 pb-20 px-6">
        <div className="max-w-4xl mx-auto">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-center mb-12">
            <button onClick={() => navigate('/')} className={`inline-flex items-center gap-2 transition-colors mb-6 ${isDark ? 'text-white/60 hover:text-white' : 'text-gray-500 hover:text-gray-900'}`}>
              <ArrowLeft className="w-4 h-4" /><span className="font-['Manrope']">{t('common.back')}</span>
            </button>
            <div className={`inline-flex items-center justify-center w-16 h-16 border mb-6 ${isDark ? 'bg-blue-500/10 border-blue-500/20' : 'bg-red-500/10 border-red-500/20'}`}>
              <Calculator className={`w-8 h-8 ${isDark ? 'text-blue-400' : 'text-red-600'}`} />
            </div>
            <h1 className={`text-4xl md:text-5xl font-['Unbounded'] font-bold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>{t('loanCalc.title')}</h1>
            <p className={`text-xl font-['Manrope'] ${isDark ? 'text-white/60' : 'text-gray-600'}`}>{t('loanCalc.description')}</p>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.2 }} className={panelCls}>
              <h2 className={`text-2xl font-['Unbounded'] font-bold mb-6 ${isDark ? 'text-white' : 'text-gray-900'}`}>{t('loanCalc.vehiclePrice')}</h2>
              <form onSubmit={calculateLoan} className="space-y-6">
                <div>
                  <Label htmlFor="loanAmount" className={`font-['Manrope'] mb-2 flex items-center gap-2 ${isDark ? 'text-white/80' : 'text-gray-700'}`}>
                    <DollarSign className="w-4 h-4" />{t('loanCalc.vehiclePrice')} ($) *
                  </Label>
                  <Input id="loanAmount" name="loanAmount" type="number" value={formData.loanAmount} onChange={handleChange} required min="0" step="100" className={inputCls} placeholder="35000" />
                </div>
                <div>
                  <Label htmlFor="downPayment" className={`font-['Manrope'] mb-2 flex items-center gap-2 ${isDark ? 'text-white/80' : 'text-gray-700'}`}>
                    <DollarSign className="w-4 h-4" />{t('loanCalc.downPayment')} ($) *
                  </Label>
                  <Input id="downPayment" name="downPayment" type="number" value={formData.downPayment} onChange={handleChange} required min="0" step="100" className={inputCls} placeholder="5000" />
                </div>
                <div>
                  <Label htmlFor="interestRate" className={`font-['Manrope'] mb-2 flex items-center gap-2 ${isDark ? 'text-white/80' : 'text-gray-700'}`}>
                    <Percent className="w-4 h-4" />{t('loanCalc.interestRate')} *
                  </Label>
                  <Input id="interestRate" name="interestRate" type="number" value={formData.interestRate} onChange={handleChange} required min="0" max="30" step="0.01" className={inputCls} placeholder="5.5" />
                </div>
                <div>
                  <Label htmlFor="loanTerm" className={`font-['Manrope'] mb-2 flex items-center gap-2 ${isDark ? 'text-white/80' : 'text-gray-700'}`}>
                    <Calendar className="w-4 h-4" />{t('loanCalc.loanTerm')} *
                  </Label>
                  <Input id="loanTerm" name="loanTerm" type="number" value={formData.loanTerm} onChange={handleChange} required min="1" max="10" step="1" className={inputCls} placeholder="5" />
                </div>
                <div className="flex gap-4 pt-4">
                  <Button type="submit" className={`flex-1 text-white font-['Manrope'] font-semibold py-6 text-lg ${isDark ? 'bg-blue-500 hover:bg-blue-600' : 'bg-red-600 hover:bg-red-700'}`}>{t('common.submit')}</Button>
                  <Button type="button" onClick={resetCalculator} className={`px-6 font-['Manrope'] ${isDark ? 'bg-white/5 hover:bg-white/10 text-white/80 border border-white/10' : 'bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300'}`}>{t('inventory.resetAll')}</Button>
                </div>
              </form>
            </motion.div>

            <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.3 }} className={panelCls}>
              <h2 className={`text-2xl font-['Unbounded'] font-bold mb-6 ${isDark ? 'text-white' : 'text-gray-900'}`}>{t('loanCalc.monthlyPayment')}</h2>
              {monthlyPayment !== null ? (
                <div className="space-y-6">
                  <div className={`p-6 border ${isDark ? 'bg-blue-500/10 border-blue-500/30' : 'bg-red-500/10 border-red-500/30'}`}>
                    <div className={`text-sm font-['Manrope'] mb-2 ${isDark ? 'text-blue-300' : 'text-red-600'}`}>{t('loanCalc.monthlyPayment')}</div>
                    <div className={`text-5xl font-['Unbounded'] font-bold mb-1 ${isDark ? 'text-white' : 'text-gray-900'}`}>${monthlyPayment.toFixed(2)}</div>
                    <div className={`text-xs font-['Manrope'] ${isDark ? 'text-white/60' : 'text-gray-500'}`}>{formData.loanTerm} {t('loanCalc.months')}</div>
                  </div>
                  <div className="space-y-4">
                    {[
                      { label: t('loanCalc.loanAmount'), value: `$${parseFloat(formData.loanAmount).toLocaleString()}` },
                      { label: t('loanCalc.downPayment'), value: `$${parseFloat(formData.downPayment).toLocaleString()}` },
                      { label: t('loanCalc.loanAmount'), value: `$${(parseFloat(formData.loanAmount) - parseFloat(formData.downPayment)).toLocaleString()}` },
                      { label: t('loanCalc.interestRate'), value: `${formData.interestRate}% APR` },
                    ].map((item, i) => (
                      <div key={i} className={`flex justify-between items-center pb-3 border-b ${isDark ? 'border-white/10' : 'border-gray-200'}`}>
                        <span className={`font-['Manrope'] ${isDark ? 'text-white/60' : 'text-gray-600'}`}>{item.label}</span>
                        <span className={`font-['Manrope'] font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>{item.value}</span>
                      </div>
                    ))}
                    <div className={`flex justify-between items-center pb-3 border-b ${isDark ? 'border-white/10' : 'border-gray-200'}`}>
                      <span className={`font-['Manrope'] ${isDark ? 'text-white/60' : 'text-gray-600'}`}>{t('loanCalc.totalInterest')}</span>
                      <span className="text-orange-400 font-['Manrope'] font-semibold">${totalInterest.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between items-center pb-3">
                      <span className={`font-['Manrope'] ${isDark ? 'text-white/60' : 'text-gray-600'}`}>{t('loanCalc.totalCost')}</span>
                      <span className="text-green-400 font-['Manrope'] font-semibold">${totalPayment.toFixed(2)}</span>
                    </div>
                  </div>
                  <div className={`mt-6 p-4 border ${isDark ? 'bg-yellow-500/5 border-yellow-500/20' : 'bg-yellow-50 border-yellow-200'}`}>
                    <p className={`text-xs font-['Manrope'] ${isDark ? 'text-yellow-300/80' : 'text-yellow-700'}`}>{t('loanCalc.disclaimer')}</p>
                  </div>
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center h-full text-center py-12">
                  <Calculator className={`w-16 h-16 mb-4 ${isDark ? 'text-white/20' : 'text-gray-300'}`} />
                  <p className={`font-['Manrope'] mb-2 ${isDark ? 'text-white/60' : 'text-gray-600'}`}>{t('loanCalc.description')}</p>
                  <p className={`text-sm font-['Manrope'] ${isDark ? 'text-white/40' : 'text-gray-400'}`}>{t('loanCalc.monthlyPayment')}</p>
                </div>
              )}
            </motion.div>
          </div>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }} className={`mt-8 ${panelCls}`}>
            <h3 className={`text-xl font-['Unbounded'] font-bold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>{t('howItWorks.title')}</h3>
            <div className={`grid grid-cols-1 md:grid-cols-3 gap-6 font-['Manrope'] ${isDark ? 'text-white/60' : 'text-gray-600'}`}>
              <div>
                <h4 className={`font-semibold mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>{t('howItWorks.step1Title')}</h4>
                <p className="text-sm">{t('howItWorks.step1Desc')}</p>
              </div>
              <div>
                <h4 className={`font-semibold mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>{t('howItWorks.step2Title')}</h4>
                <p className="text-sm">{t('howItWorks.step2Desc')}</p>
              </div>
              <div>
                <h4 className={`font-semibold mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>{t('howItWorks.step3Title')}</h4>
                <p className="text-sm">{t('howItWorks.step3Desc')}</p>
              </div>
            </div>
          </motion.div>
        </div>
      </div>

      <Footer />
    </div>
  );
}
